import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  FlexRowBetween,
  Input,
  Label,
  PopupForm,
  TextArea,
} from "../../common/Style";
import { useForm } from "react-hook-form";
import { CFV } from "../../common/others/Functions";
import OpacityLoading from "../../common/loader/OpacityLoading";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { add } from "../../../store/reducers/todosReducer";
import GCalendar from "../../common/calendar/Calendar";
import GenericModal from "../../common/modals/GenericModal";

// סוגי הודעות בלו"ז המובנה – כל אחד יציג תצוגה מקדימה (תמונה ב-public/message-previews)
// הסדר חובה: ... → ווצאפ שלישי → שיחות 1–4 → קדם תזכורת → תזכורות → תודה
export const ROLE_6_MESSAGE_TYPES = [
  { id: "sms_first", label: "הודעה ראשונה" },
  { id: "sms_second", label: "הודעה שניה" },
  { id: "whatsapp_first", label: "ווצאפ ראשון" },
  { id: "whatsapp_second", label: "ווצאפ שני" },
  { id: "whatsapp_third", label: "ווצאפ שלישי" },
  { id: "call_first", label: "שיחה ראשונה" },
  { id: "call_second", label: "שיחה שניה" },
  { id: "call_third", label: "שיחה שלישית" },
  { id: "call_fourth", label: "שיחה רביעית" },
  { id: "pre_reminder", label: "קדם תזכורת" },
  { id: "sms_reminder", label: "תזכורת סמס" },
  { id: "whatsapp_reminder", label: "תזכורת ווצאפ" },
  { id: "thank_you_sms", label: "תודה" },
];

/** כותרות מלו"ז המובנה – ולידציית תאריכים/סדר חלה עליהן לפי הכותרת בלבד, לכל המשתמשים */
export const SCHEDULE_MESSAGE_LABELS = new Set(
  ROLE_6_MESSAGE_TYPES.map((t) => t.label),
);

export const CALL_TASK_IDS = new Set(
  ROLE_6_MESSAGE_TYPES.filter((t) => t.id.startsWith("call_")).map((t) => t.id),
);

export const CALL_TASK_BASE_LABELS = ROLE_6_MESSAGE_TYPES.filter((t) =>
  CALL_TASK_IDS.has(t.id),
).map((t) => t.label);

/** בוקר / צהריים / ערב מוצמדים לכותרת משימת שיחה; חופשי = ללא סיומת */
export const CALL_TIME_PRIORITY_OPTIONS = [
  { id: "free", label: "חופשי", suffix: "" },
  { id: "morning", label: "בוקר", suffix: "בוקר" },
  { id: "noon", label: "צהריים", suffix: "צהריים" },
  { id: "evening", label: "ערב", suffix: "ערב" },
];

const CALL_TIME_SUFFIX_SCAN_ORDER = ["צהריים", "בוקר", "ערב"];

/** כותרת בסיס לוגית לשיחות (ללא סיומת עדיפות), או הכותרת כפי שהיא */
export function getCallTaskBaseTitle(fullTitle) {
  const t = String(fullTitle || "").trim();
  for (const suf of CALL_TIME_SUFFIX_SCAN_ORDER) {
    const tail = " " + suf;
    if (t.endsWith(tail)) {
      const base = t.slice(0, -tail.length).trim();
      if (CALL_TASK_BASE_LABELS.includes(base)) return base;
    }
  }
  return t;
}

export function parseCallTaskPriorityIdFromTitle(fullTitle) {
  const t = String(fullTitle || "").trim();
  for (const suf of CALL_TIME_SUFFIX_SCAN_ORDER) {
    const tail = " " + suf;
    if (t.endsWith(tail)) {
      const base = t.slice(0, -tail.length).trim();
      if (CALL_TASK_BASE_LABELS.includes(base)) {
        const opt = CALL_TIME_PRIORITY_OPTIONS.find((o) => o.suffix === suf);
        return opt ? opt.id : "free";
      }
    }
  }
  if (CALL_TASK_BASE_LABELS.includes(t)) return "free";
  return "free";
}

export function buildCallTaskTitle(baseLabel, priorityId) {
  if (!CALL_TASK_BASE_LABELS.includes(baseLabel)) return baseLabel;
  const opt = CALL_TIME_PRIORITY_OPTIONS.find((o) => o.id === priorityId);
  const suf = opt?.suffix ?? "";
  return suf ? `${baseLabel} ${suf}` : baseLabel;
}

/** כותרת שמתאימה לרשימת סוגי הלו״ז המובנה (כולל משימות שיחה עם סיומת בוקר/צהריים/ערב) */
export function isStandardScheduleMessageTitle(rawTitle) {
  const t = String(rawTitle || "").trim();
  if (!t) return true;
  if (ROLE_6_MESSAGE_TYPES.some((x) => x.label === t)) return true;
  const base = getCallTaskBaseTitle(t);
  return ROLE_6_MESSAGE_TYPES.some((x) => x.label === base);
}

const ROLE_5_PACKAGE_025 = [
  "הודעה ראשונה",
  "הודעה שניה",
  "קדם תזכורת",
  "תזכורת סמס",
  "תודה",
];
const ROLE_5_PACKAGE_065_EXTRA = [
  "ווצאפ ראשון",
  "ווצאפ שני",
  "ווצאפ שלישי",
  "תזכורת ווצאפ",
];
const ROLE_5_PACKAGE_125_EXTRA = [
  "שיחה ראשונה",
  "שיחה שניה",
  "שיחה שלישית",
  "שיחה רביעית",
];

/** מזהה deal תקין ל־role 5 (השוואה צפופה לערכי עשרוניים) */
export const normalizeRole5Deal = (deal) => {
  const n = Number(deal);
  if (!Number.isFinite(n)) return null;
  if (Math.abs(n - 0.25) < 0.01) return 0.25;
  if (Math.abs(n - 0.65) < 0.01) return 0.65;
  if (Math.abs(n - 1.25) < 0.01) return 1.25;
  return null;
};

/** Set של כותרות משימות מותרות לפי חבילת role 5, או null אם ה־deal לא מוכר */
export const getAllowedLabelsForRole5Deal = (deal) => {
  const tier = normalizeRole5Deal(deal);
  if (tier == null) return null;
  const labels = [...ROLE_5_PACKAGE_025];
  if (tier >= 0.65) labels.push(...ROLE_5_PACKAGE_065_EXTRA);
  if (tier >= 1.25) labels.push(...ROLE_5_PACKAGE_125_EXTRA);
  return new Set(labels);
};

/**
 * כותרות מסוגי החבילה שעדיין לא קיימות בלו״ז (למשתמש role 5).
 * אם אין חבילה מזוהה — מערך ריק (אין מה לאכוף).
 */
export const getRole5PackageMissingLabels = (deal, todos) => {
  const allowed = getAllowedLabelsForRole5Deal(deal);
  if (allowed == null) return [];
  const list = Array.isArray(todos) ? todos : Object.values(todos || {});
  const existing = new Set(
    list
      .map((t) => getCallTaskBaseTitle(String(t?.title || "").trim()))
      .filter(Boolean),
  );
  return [...allowed].filter((label) => !existing.has(label));
};

/** האם משתמש role 5 רשאי לתזמן הודעות (כל סוגי החבילה כבר בלו״ז) */
export const canRole5ScheduleMessages = (user, todos) => {
  if (user?.role !== 5) return true;
  return getRole5PackageMissingLabels(user?.deal, todos).length === 0;
};

/** מינימום חבילה (מחיר) שבו סוג משימה נכלל; null = לא ממופה לחבילות role 5. נשמר לשחזור UI סיכום חבילה. */
export const getMinDealForScheduleLabel = (label) => {
  if (ROLE_5_PACKAGE_025.includes(label)) return 0.25;
  if (ROLE_5_PACKAGE_065_EXTRA.includes(label)) return 0.65;
  if (ROLE_5_PACKAGE_125_EXTRA.includes(label)) return 1.25;
  return null;
};

/*
 * UI סיכום חבילה (כלול / לא כלול) — הוסר מהמסך לבקשת משתמש; נשמר כאן לשחזור.
 * import מ־Edit הוסר; להחזיר: export const Role5PackageInfo = ...
 *
export const Role5PackageInfo = ({ deal }) => {
  const allowed = getAllowedLabelsForRole5Deal(deal);
  const tier = normalizeRole5Deal(deal);
  if (!allowed || tier == null) return null;
  const included = ROLE_6_MESSAGE_TYPES.filter((t) => allowed.has(t.label));
  const excluded = ROLE_6_MESSAGE_TYPES.filter((t) => !allowed.has(t.label));
  return (
    <div style={{ marginBottom: "16px", fontSize: "13px", lineHeight: 1.55, textAlign: "right", direction: "rtl", padding: "12px", background: "#f5f8fc", borderRadius: "8px", border: "1px solid #d0d8e8" }}>
      <div style={{ fontWeight: 600, marginBottom: "10px", color: "#1a237e" }}>
        החבילה שלך ({tier}): מה אפשר להוסיף ומה לא
      </div>
      <div style={{ marginBottom: "12px", color: "#1b5e20" }}>
        <span style={{ fontWeight: 600 }}>כלול בחבילה — </span>
        {included.map((t) => t.label).join(" · ")}
      </div>
      <div style={{ color: "#555" }}>
        <div style={{ fontWeight: 600, marginBottom: "6px", color: "#b71c1c" }}>לא כלול בחבילה הנוכחית:</div>
        <ul style={{ margin: 0, paddingRight: "20px" }}>
          {excluded.map((t) => {
            const minD = getMinDealForScheduleLabel(t.label);
            const hint =
              minD == null
                ? "לא זמין בחבילות המוצעות (למשל מסלול מורחב)"
                : minD > tier
                  ? `זמין מחבילה ${minD} ומעלה`
                  : "";
            return (
              <li key={t.id} style={{ marginBottom: "4px" }}>
                {t.label}
                {hint ? ` — ${hint}` : ""}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};
*/

/** מחזיר YYYY-MM-DD לתאריך (להשוואת ימים). */
export const getTaskDateStr = (executionDate) => {
  if (!executionDate) return "";
  const d =
    typeof executionDate === "string" ? new Date(executionDate) : executionDate;
  return d.toISOString().slice(0, 10);
};

const ROLE_6_BEFORE_EVENT_TYPES = [
  "הודעה ראשונה",
  "הודעה שניה",
  "ווצאפ ראשון",
  "ווצאפ שני",
  "ווצאפ שלישי",
  "שיחה ראשונה",
  "שיחה שניה",
  "שיחה שלישית",
  "שיחה רביעית",
  "קדם תזכורת",
];

/** מספר ימים לוחדיים מתאריך ביצוע עד תאריך אירוע (חיובי אם הביצוע לפני האירוע) */
export const calendarDaysFromExecToEvent = (executionDate, eventDate) => {
  const execStr = getTaskDateStr(executionDate);
  const eventStr = getTaskDateStr(eventDate);
  if (!execStr || !eventStr) return null;
  const t0 = new Date(`${execStr}T12:00:00`).getTime();
  const t1 = new Date(`${eventStr}T12:00:00`).getTime();
  return Math.round((t1 - t0) / 86400000);
};

/**
 * בודק תאריך עבור משימות role 6 לפי סוג:
 * - הודעה ראשונה/שניה, ווצאפ ראשון/שני/שלישי, שיחות 1–4, קדם תזכורת: עד תאריך האירוע (לא אחריו).
 * - קדם תזכורת: רק בין 6 ימים לפני האירוע לבין 2 ימים לפני (כולל).
 * - תזכורת סמס / תזכורת ווצאפ: רק בתאריך האירוע.
 * - תודה: רק בתאריך האירוע או אחריו.
 */
export const validateRole6ReminderAndThankDate = (
  eventDate,
  executionDate,
  title,
) => {
  if (!eventDate) return { valid: true };
  const eventStr = getTaskDateStr(eventDate);
  const execStr = getTaskDateStr(executionDate);
  if (!eventStr || !execStr) return { valid: true };

  const titleBase = getCallTaskBaseTitle(title);
  if (ROLE_6_BEFORE_EVENT_TYPES.includes(titleBase)) {
    if (execStr > eventStr) {
      return {
        valid: false,
        message: "לא ניתן להוסיף משימה מסוג זה בתאריך לאחר תאריך האירוע",
      };
    }
  }
  if (title === "קדם תזכורת") {
    const daysUntil = calendarDaysFromExecToEvent(executionDate, eventDate);
    if (daysUntil == null) return { valid: true };
    if (daysUntil < 2 || daysUntil > 6) {
      return {
        valid: false,
        message:
          "ניתן להוסיף קדם תזכורת רק בין 6 ימים לפני האירוע לבין 2 ימים לפני האירוע (כולל)",
      };
    }
  }
  if (title === "תזכורת סמס" || title === "תזכורת ווצאפ") {
    if (execStr !== eventStr) {
      return {
        valid: false,
        message: "ניתן להוסיף תזכורת רק בתאריך האירוע",
      };
    }
  }
  if (title === "תודה") {
    if (execStr < eventStr) {
      return {
        valid: false,
        message: "ניתן להוסיף תודה רק בתאריך האירוע או אחריו",
      };
    }
  }
  return { valid: true };
};

/**
 * מחזיר את קבוצת הכותרות (סוגי הודעות) שכבר קיימות בלו"ז – כל סוג יכול להופיע פעם אחת בלבד.
 */
export const getExistingLabelsInSchedule = (todos, excludeTodoId = null) => {
  const list = Array.isArray(todos) ? todos : Object.values(todos || {});
  const filtered = list.filter((t) => t._id !== excludeTodoId);
  return new Set(filtered.map((t) => t.title));
};

/**
 * בודק שסדר התאריכים של משימות הלו״ז המובנה נשמר (לכל המשתמשים):
 * תאריך הודעה ראשונה <= תאריך הודעה שניה <= תאריך ווצאפ ראשון <= ... <= תאריך תודה.
 * בהוספה/עריכה: התאריך של המשימה הנוכחית חייב להיות גדול או שווה מכל סוג קודם, וקטן או שווה מכל סוג מאוחר.
 * כותרת שאינה ברשימה המובנית — לא נחסמת.
 * @param todos - אובייקט או מערך של todos (של המשתמש)
 * @param executionDate - תאריך הביצוע של המשימה שנוספת/מתערכת
 * @param newTitle - כותרת המשימה (סוג ההודעה)
 * @param excludeTodoId - בעריכה: _id של המשימה שמעריכים (לא נספרת)
 */
export const validateRole6TaskOrder = (
  todos,
  executionDate,
  newTitle,
  excludeTodoId = null,
) => {
  const todosList = Array.isArray(todos) ? todos : Object.values(todos || {});
  const existing = todosList.filter((t) => t._id !== excludeTodoId);
  const dateStrNew = getTaskDateStr(executionDate);
  if (!dateStrNew) return { valid: true };

  const labels = ROLE_6_MESSAGE_TYPES.map((t) => t.label);
  const minDateStrByLabel = {};
  const maxDateStrByLabel = {};
  existing.forEach((t) => {
    const ds = getTaskDateStr(t.executionDate);
    if (!ds) return;
    const key = getCallTaskBaseTitle(t.title);
    if (minDateStrByLabel[key] == null || ds < minDateStrByLabel[key]) {
      minDateStrByLabel[key] = ds;
    }
    if (maxDateStrByLabel[key] == null || ds > maxDateStrByLabel[key]) {
      maxDateStrByLabel[key] = ds;
    }
  });

  const newTitleBase = getCallTaskBaseTitle(newTitle);
  const idx = labels.indexOf(newTitleBase);
  if (idx < 0) return { valid: true };

  // הודעות, ווצאפים 1–3 ושיחות 1–4 – רק משימה אחת מקבוצה זו בכל יום
  const onePerDayTypes = [
    "הודעה ראשונה",
    "הודעה שניה",
    "ווצאפ ראשון",
    "ווצאפ שני",
    "ווצאפ שלישי",
    "שיחה ראשונה",
    "שיחה שניה",
    "שיחה שלישית",
    "שיחה רביעית",
  ];
  if (onePerDayTypes.includes(newTitleBase)) {
    const otherOnSameDay = existing.some(
      (t) =>
        onePerDayTypes.includes(getCallTaskBaseTitle(t.title)) &&
        getTaskDateStr(t.executionDate) === dateStrNew,
    );
    if (otherOnSameDay) {
      return {
        valid: false,
        message:
          "הודעות, ווצאפים ושיחות מסוג זה חייבים להיות בימים שונים זה מזה. בתאריך זה כבר קיימת משימה מהקבוצה.",
      };
    }
  }

  // סוגים קודמים: התאריך (היום) המוקדם ביותר שלהם חייב להיות <= התאריך שבחרת
  for (let i = 0; i < idx; i++) {
    const label = labels[i];
    const minDs = minDateStrByLabel[label];
    if (minDs != null && minDs > dateStrNew) {
      const displayDate = minDs.split("-").reverse().join("/");
      return {
        valid: false,
        message: `תאריך "${label}" (${displayDate}) חייב להיות לפני התאריך שבחרת`,
      };
    }
  }

  // סוגים מאוחרים: התאריך (היום) המאוחר ביותר שלהם חייב להיות >= התאריך שבחרת
  for (let i = idx + 1; i < labels.length; i++) {
    const label = labels[i];
    const maxDs = maxDateStrByLabel[label];
    if (maxDs != null && maxDs < dateStrNew) {
      const displayDate = maxDs.split("-").reverse().join("/");
      return {
        valid: false,
        message: `תאריך "${label}" (${displayDate}) חייב להיות אחרי התאריך שבחרת`,
      };
    }
  }

  // תודה: חייבת להיות לפחות משימת תזכורת אחת (סמס או ווצאפ) עם תאריך <= התאריך שבחרת
  if (newTitleBase === "תודה") {
    const minReminderDs =
      minDateStrByLabel["תזכורת סמס"] != null &&
      minDateStrByLabel["תזכורת ווצאפ"] != null
        ? minDateStrByLabel["תזכורת סמס"] < minDateStrByLabel["תזכורת ווצאפ"]
          ? minDateStrByLabel["תזכורת סמס"]
          : minDateStrByLabel["תזכורת ווצאפ"]
        : (minDateStrByLabel["תזכורת סמס"] ??
          minDateStrByLabel["תזכורת ווצאפ"]);
    if (minReminderDs == null) {
      return {
        valid: false,
        message:
          "יש להוסיף קודם לפחות משימת תזכורת אחת (תזכורת סמס או תזכורת ווצאפ)",
      };
    }
    if (minReminderDs > dateStrNew) {
      return {
        valid: false,
        message: "תאריך משימת התזכורת חייב להיות לפני התאריך שבחרת",
      };
    }
  }

  return { valid: true };
};

/**
 * נתיב תצוגה מקדימה ב־public/message-previews.
 * כרגע ה־UI של התצוגה המקדימה מושבת (SHOW_MESSAGE_TYPE_PREVIEW); הפונקציה נשמרת לשחזור.
 */
const role6MessagePreviewSrc = (typeId) =>
  typeId === "pre_reminder"
    ? "/message-previews/pre_reminder.png"
    : `/message-previews/${typeId}.jpeg`;

/** false = לא מציגים תמונת תצוגה מקדימה; להחזיר: true */
const SHOW_MESSAGE_TYPE_PREVIEW = false;

const Add = ({ init, producer, userRole, isTal, eventDate, userDeal }) => {
  const dispatch = useDispatch();
  const { todos, todosLoading } = useSelector((state) => state.todosReducer);
  const isRole6 = userRole === 6 || (userRole === 1 && isTal);
  const role5PackageAllowed =
    userRole === 5 ? getAllowedLabelsForRole5Deal(userDeal) : null;
  const useStructuredSchedule = isRole6 || role5PackageAllowed != null;

  const defaultFormValues = useMemo(() => {
    const isR6 = userRole === 6 || (userRole === 1 && isTal);
    const pkg = userRole === 5 ? getAllowedLabelsForRole5Deal(userDeal) : null;
    const structured = isR6 || pkg != null;
    if (!structured)
      return { title: "", callTimePriority: "free", talCustomTitle: "" };
    const first =
      ROLE_6_MESSAGE_TYPES.find((t) => pkg == null || pkg.has(t.label)) ??
      ROLE_6_MESSAGE_TYPES[0];
    return {
      title: first.label,
      messageTypeId: first.id,
      callTimePriority: "free",
      talCustomTitle: isTal ? first.label : "",
    };
  }, [userRole, userDeal, isTal]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    getValues,
  } = useForm({
    defaultValues: defaultFormValues,
  });
  const [showCalendar, setShowCalendar] = useState(null);
  const [talCustomTitleEnabled, setTalCustomTitleEnabled] = useState(false);
  const [date, setDate] = useState(new Date());
  const existingLabelsInSchedule = getExistingLabelsInSchedule(todos, null);
  const existingTitleBases = useMemo(
    () =>
      new Set(
        [...existingLabelsInSchedule].map((x) => getCallTaskBaseTitle(x)),
      ),
    [existingLabelsInSchedule],
  );

  const selectedMessageTypeId = useStructuredSchedule
    ? watch("messageTypeId")
    : null;
  const selectedMessageType =
    useStructuredSchedule && selectedMessageTypeId
      ? ROLE_6_MESSAGE_TYPES.find((t) => t.id === selectedMessageTypeId)
      : null;

  const selectedPlainTitle = !useStructuredSchedule ? watch("title") : "";
  const talCustomTitleWatch = isTal ? watch("talCustomTitle") : "";
  const talTrimmedWatch = isTal ? String(talCustomTitleWatch || "").trim() : "";
  const talCustomBaseForPriority =
    isTal && talTrimmedWatch ? getCallTaskBaseTitle(talTrimmedWatch) : "";
  const showCallTimePriority =
    (useStructuredSchedule &&
      ((selectedMessageTypeId && CALL_TASK_IDS.has(selectedMessageTypeId)) ||
        (talTrimmedWatch &&
          CALL_TASK_BASE_LABELS.includes(talCustomBaseForPriority)))) ||
    (!useStructuredSchedule &&
      (CALL_TASK_BASE_LABELS.includes(
        String(selectedPlainTitle || "").trim(),
      ) ||
        (talTrimmedWatch &&
          CALL_TASK_BASE_LABELS.includes(talCustomBaseForPriority))));

  useEffect(() => {
    if (!isTal || talCustomTitleEnabled) return;
    const label = useStructuredSchedule
      ? (ROLE_6_MESSAGE_TYPES.find((t) => t.id === selectedMessageTypeId)
          ?.label ?? "")
      : String(selectedPlainTitle || "").trim();
    setValue("talCustomTitle", label, {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [
    isTal,
    talCustomTitleEnabled,
    selectedMessageTypeId,
    selectedPlainTitle,
    useStructuredSchedule,
    setValue,
  ]);

  const handleTalTypeLabelContextMenu = (e) => {
    if (!isTal) return;
    e.preventDefault();
    setTalCustomTitleEnabled((prev) => {
      if (prev) {
        const label = useStructuredSchedule
          ? (ROLE_6_MESSAGE_TYPES.find((t) => t.id === selectedMessageTypeId)
              ?.label ?? "")
          : String(selectedPlainTitle || "").trim();
        setValue("talCustomTitle", label, {
          shouldDirty: false,
          shouldValidate: false,
        });
      }
      return !prev;
    });
  };

  const handleSetDate = (e) => {
    let date1 = e;
    date1.setHours(20);
    setDate(date1);
  };

  const onSubmit = async (data) => {
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (selectedDate < today) {
      toast.warning("לא ניתן לבחור תאריך עבר למשימה");
      return;
    }

    let title;
    const rawTal = String(
      data.talCustomTitle ?? getValues("talCustomTitle") ?? "",
    ).trim();
    const talTrim = isTal && talCustomTitleEnabled && rawTal ? rawTal : "";

    const applyCallPriorityIfCallTask = (t) => {
      const base = getCallTaskBaseTitle(t);
      if (CALL_TASK_BASE_LABELS.includes(base)) {
        return buildCallTaskTitle(base, data.callTimePriority || "free");
      }
      return t;
    };

    if (useStructuredSchedule) {
      if (talTrim) {
        title = applyCallPriorityIfCallTask(talTrim);
      } else {
        title =
          ROLE_6_MESSAGE_TYPES.find((t) => t.id === data.messageTypeId)
            ?.label ?? "";
        title = String(title).trim();
        if (!title) {
          toast.warning("יש לבחור סוג הודעה מהרשימה");
          return;
        }
        if (CALL_TASK_IDS.has(data.messageTypeId)) {
          title = buildCallTaskTitle(title, data.callTimePriority || "free");
        }
      }
      const listLabels = new Set(ROLE_6_MESSAGE_TYPES.map((t) => t.label));
      const titleBase = getCallTaskBaseTitle(title);
      const packageKey = listLabels.has(title)
        ? title
        : listLabels.has(titleBase)
          ? titleBase
          : null;
      if (
        role5PackageAllowed != null &&
        packageKey != null &&
        !role5PackageAllowed.has(packageKey)
      ) {
        toast.warning("סוג משימה זה לא כלול בחבילה שלך");
        return;
      }
      if (packageKey != null && existingTitleBases.has(packageKey)) {
        toast.warning(`משימה מסוג "${packageKey}" כבר קיימת בלו"ז`);
        return;
      }
    } else {
      if (talTrim) {
        title = applyCallPriorityIfCallTask(talTrim);
      } else {
        title = String(data.title || "").trim();
        if (!title) {
          toast.warning("יש לבחור סוג משימה מהרשימה");
          return;
        }
      }
      const titleBaseForRules = getCallTaskBaseTitle(title);
      if (SCHEDULE_MESSAGE_LABELS.has(titleBaseForRules)) {
        const existingOther = getExistingLabelsInSchedule(todos, null);
        const existingBases = new Set(
          [...existingOther].map((x) => getCallTaskBaseTitle(x)),
        );
        if (existingBases.has(titleBaseForRules)) {
          toast.warning(`משימה מסוג "${titleBaseForRules}" כבר קיימת בלו"ז`);
          return;
        }
        if (CALL_TASK_BASE_LABELS.includes(title)) {
          title = buildCallTaskTitle(title, data.callTimePriority || "free");
        }
      } else if (CALL_TASK_BASE_LABELS.includes(title)) {
        title = buildCallTaskTitle(title, data.callTimePriority || "free");
      }
    }

    // ולידציית סדר/תאריכים חלה על כולם — ללא תלות ב־role / deal / isTal
    const dateCheck = validateRole6ReminderAndThankDate(
      eventDate,
      date.toISOString(),
      title,
    );
    if (!dateCheck.valid) {
      toast.warning(dateCheck.message);
      return;
    }
    const orderCheck = validateRole6TaskOrder(
      todos,
      date.toISOString(),
      title,
      null,
    );
    if (!orderCheck.valid) {
      toast.warning(orderCheck.message);
      return;
    }
    const body = {
      ...data,
      title,
      executionDate: date.toISOString(),
      company: producer,
      ...(useStructuredSchedule && { description: "" }),
    };
    if (useStructuredSchedule) delete body.messageTypeId;
    delete body.callTimePriority;
    delete body.talCustomTitle;
    dispatch(add(body)).then((res) => {
      if (!res.payload?.error) {
        toast.success("הפעולה בוצעה בהצלחה");
        if (isTal) {
          setTalCustomTitleEnabled(false);
          const label = useStructuredSchedule
            ? (ROLE_6_MESSAGE_TYPES.find(
                (t) => t.id === getValues("messageTypeId"),
              )?.label ?? "")
            : String(getValues("title") || "").trim();
          setValue("talCustomTitle", label);
        }
        init();
      } else toast.error(res.payload?.error);
    });
  };

  return (
    <PopupForm
      onClick={(e) => e.stopPropagation()}
      onSubmit={handleSubmit(onSubmit)}
      className="animate__animated animate__fadeIn"
    >
      <div
        style={{
          width: "90%",
          margin: "15px auto",
        }}
      >
        {useStructuredSchedule ? (
          <>
            <div
              style={{
                fontSize: "14px",
                lineHeight: 1.5,
                color: "#37474f",
                marginBottom: "16px",
                textAlign: "right",
                direction: "rtl",
                padding: "10px 12px",
                background: "#eceff1",
                borderRadius: "8px",
              }}
            >
              נוסחי ההודעות מופיעים במסמך תיאום הציפיות שבעמוד ההדרכות.
            </div>
            {/*
              בעבר (role 6 כמנהל / isTal): שדה כותרת חופשית + רשימת "בחירה מהירה" נפרדת.
              בעבר (מובנה אחר): רק select לפי messageTypeId.
              כרגע: רק select אחד. להחזיר כותרת חופשית — שחרר את הבלוק המוערת והתאם defaultFormValues + onSubmit.
              {isRole6 && isTal ? (
                <>
                  <Label>כותרת</Label>
                  <Input
                    type="text"
                    {...register("title", {
                      required: true,
                      minLength: 2,
                      maxLength: 50,
                    })}
                  />
                  {CFV("title", "בין 2 ל־50 תווים", errors)}
                  <Label style={{ marginTop: "12px", display: "block" }}>
                    בחירה מהירה מהרשימה
                  </Label>
                  <select
                    onChange={(e) => {
                      const option = ROLE_6_MESSAGE_TYPES.find(
                        (t) => t.id === e.target.value,
                      );
                      if (option)
                        setValue("title", option.label, {
                          shouldValidate: true,
                        });
                    }}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      fontSize: "16px",
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      backgroundColor: "#fff",
                      marginBottom: "8px",
                    }}
                  >
                    <option value="">— בחר להזין כותרת —</option>
                    {ROLE_6_MESSAGE_TYPES.map((type) => {
                      const blockedByPackage =
                        role5PackageAllowed != null &&
                        !role5PackageAllowed.has(type.label);
                      const disabled =
                        existingLabelsInSchedule.has(type.label) ||
                        blockedByPackage;
                      return (
                        <option key={type.id} value={type.id} disabled={disabled}>
                          {type.label}
                          {existingLabelsInSchedule.has(type.label)
                            ? ' (קיים בלו"ז)'
                            : blockedByPackage
                              ? " (לא כלול בחבילה)"
                              : ""}
                        </option>
                      );
                    })}
                  </select>
                </>
              ) : (
                <>
                  אותו מבנה כמו ה-select החי למטה: Label &quot;סוג הודעה&quot; + register(messageTypeId) + מיפוי ROLE_6_MESSAGE_TYPES (חסימת חבילה / קיים בלו&quot;ז).
                </>
              )}
            */}
            <Label
              onContextMenu={isTal ? handleTalTypeLabelContextMenu : undefined}
            >
              סוג ההודעה
            </Label>
            <select
              {...register("messageTypeId", { required: true })}
              style={{
                width: "100%",
                padding: "10px 12px",
                fontSize: "16px",
                border: "1px solid #ddd",
                borderRadius: "6px",
                backgroundColor: "#fff",
                marginBottom: "8px",
              }}
            >
              {ROLE_6_MESSAGE_TYPES.map((type) => {
                const blockedByPackage =
                  role5PackageAllowed != null &&
                  !role5PackageAllowed.has(type.label);
                const disabled =
                  existingTitleBases.has(type.label) || blockedByPackage;
                return (
                  <option key={type.id} value={type.id} disabled={disabled}>
                    {type.label}
                    {existingTitleBases.has(type.label)
                      ? ' (קיים בלו"ז)'
                      : blockedByPackage
                        ? " (לא כלול בחבילה)"
                        : ""}
                  </option>
                );
              })}
            </select>
            {CFV("messageTypeId", "יש לבחור סוג הודעה", errors)}
            {isTal ? (
              <div style={{ marginTop: "12px" }}>
                <Label style={{ display: "block" }}>כותרת חופשית (מנהל)</Label>
                <Input
                  type="text"
                  autoComplete="off"
                  {...register("talCustomTitle", {
                    maxLength: 50,
                    validate: (v) => {
                      const s = String(v || "").trim();
                      if (!s) return true;
                      if (s.length < 2) return "בין 2 ל־50 תווים";
                      return true;
                    },
                  })}
                  disabled={!talCustomTitleEnabled}
                />
                {CFV("talCustomTitle", "בין 2 ל־50 תווים", errors)}
              </div>
            ) : null}
            {/* {showCallTimePriority && useStructuredSchedule ? (
              <>
                <Label style={{ marginTop: "12px", display: "block" }}>
                  עדיפות לביצוע בשעות היום
                </Label>
                <select
                  {...register("callTimePriority")}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    fontSize: "16px",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    backgroundColor: "#fff",
                    marginBottom: "8px",
                  }}
                >
                  {CALL_TIME_PRIORITY_OPTIONS.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </>
            ) : null} */}

            {/*
              תצוגה מקדימה (תמונה לפי סוג הודעה) — מושבת זמנית.
              להחזיר: הגדר SHOW_MESSAGE_TYPE_PREVIEW = true למעלה בקובץ, או החלף את התנאי למטה.
            */}
            {SHOW_MESSAGE_TYPE_PREVIEW && selectedMessageType ? (
              <>
                <Label style={{ marginTop: "16px", display: "block" }}>
                  תצוגה מקדימה – מה האורחים יקבלו
                </Label>
                <div
                  style={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    padding: "12px",
                    minHeight: "120px",
                    backgroundColor: "#f9f9f9",
                    marginBottom: "16px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#555",
                      marginBottom: "8px",
                    }}
                  >
                    {selectedMessageType.label}
                  </div>
                  <img
                    src={role6MessagePreviewSrc(selectedMessageType.id)}
                    alt={`תצוגה מקדימה - ${selectedMessageType.label}`}
                    onError={(e) => {
                      e.target.style.display = "none";
                      const fallback = e.target.nextElementSibling;
                      if (fallback) fallback.style.display = "block";
                    }}
                    style={{ maxWidth: "100%", borderRadius: "6px" }}
                  />
                  <div
                    className="message-preview-placeholder"
                    style={{
                      display: "none",
                      fontSize: "13px",
                      color: "#888",
                      padding: "20px",
                      textAlign: "center",
                    }}
                  >
                    תמונת תצוגה מקדימה תופיע כאן (יש להוסיף קובץ{" "}
                    <code>
                      {selectedMessageType.id === "pre_reminder"
                        ? "pre_reminder.png"
                        : `${selectedMessageType.id}.jpeg`}
                    </code>{" "}
                    בתיקיית public/message-previews)
                  </div>
                </div>
              </>
            ) : null}
          </>
        ) : (
          <>
            {/*
              בעבר: שדה כותרת חופשית (Input, 2–50 תווים).
              להחזיר: שחרר את הבלוק המוערת; אפשר להסיר את ה-select או לשלב שני מצבי UI.
              <Label>כותרת</Label>
              <Input
                type="text"
                {...register("title", {
                  required: true,
                  minLength: 2,
                  maxLength: 50,
                })}
              />
              {CFV("title", "בין 2 ל 50 תווים", errors)}
            */}
            <Label
              onContextMenu={isTal ? handleTalTypeLabelContextMenu : undefined}
            >
              סוג משימה
            </Label>
            <select
              {...register("title", { required: true })}
              style={{
                width: "100%",
                padding: "10px 12px",
                fontSize: "16px",
                border: "1px solid #ddd",
                borderRadius: "6px",
                backgroundColor: "#fff",
                marginBottom: "8px",
              }}
            >
              <option value="">— בחר סוג משימה —</option>
              {ROLE_6_MESSAGE_TYPES.map((type) => {
                const dupBlocked =
                  SCHEDULE_MESSAGE_LABELS.has(type.label) &&
                  existingTitleBases.has(type.label);
                return (
                  <option
                    key={type.id}
                    value={type.label}
                    disabled={dupBlocked}
                  >
                    {type.label}
                    {dupBlocked ? ' (קיים בלו"ז)' : ""}
                  </option>
                );
              })}
            </select>
            {CFV("title", "יש לבחור סוג משימה", errors)}
            {isTal ? (
              <div style={{ marginTop: "12px" }}>
                <Label style={{ display: "block" }}>כותרת חופשית (מנהל)</Label>
                <Input
                  type="text"
                  autoComplete="off"
                  {...register("talCustomTitle", {
                    maxLength: 50,
                    validate: (v) => {
                      const s = String(v || "").trim();
                      if (!s) return true;
                      if (s.length < 2) return "בין 2 ל־50 תווים";
                      return true;
                    },
                  })}
                  disabled={!talCustomTitleEnabled}
                />
                {CFV("talCustomTitle", "בין 2 ל־50 תווים", errors)}
              </div>
            ) : null}
            {/* {showCallTimePriority && !useStructuredSchedule ? (
              <>
                <Label style={{ marginTop: "12px", display: "block" }}>
                  עדיפות לביצוע בשעות היום
                </Label>
                <select
                  {...register("callTimePriority")}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    fontSize: "16px",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    backgroundColor: "#fff",
                    marginBottom: "8px",
                  }}
                >
                  {CALL_TIME_PRIORITY_OPTIONS.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </>
            ) : null} */}
          </>
        )}

        {/* {!useStructuredSchedule && (
          <>
            <Label>הערות</Label>
            <TextArea
              type="text"
              rows="3"
              {...register("description", {
                minLength: 0,
                maxLength: 500,
              })}
            />
            {CFV("description", "עד 500 תווים", errors)}
          </>
        )} */}

        <Label style={{ marginTop: "10px" }}>תאריך לביצוע</Label>
        <Input
          onClick={() => setShowCalendar(true)}
          value={date
            .toISOString()
            .replace(/T.*/, "")
            .split("-")
            .reverse()
            .join("/")}
          onChange={() => {}}
          type={"text"}
        />

        <FlexRowBetween style={{ marginTop: "50px" }}>
          <Button style={{ width: "100%" }}>הוספה</Button>
        </FlexRowBetween>
      </div>

      {todosLoading && <OpacityLoading />}

      {showCalendar && (
        <GenericModal
          init={setShowCalendar}
          div={
            <GCalendar
              setDate={handleSetDate}
              date={date}
              init={setShowCalendar}
            />
          }
        />
      )}
    </PopupForm>
  );
};

export default Add;
